/**
 * InlineEdit — click-to-edit primitive for the Product Dev detail page.
 *
 * Display mode: shows the value (or placeholder) with a hover background +
 * cursor change. Edit mode: replaces the value with an input, autoFocus,
 * blur-saves, Enter-saves (Cmd/Ctrl+Enter for textarea), Esc-cancels.
 *
 * Two flavours:
 *   - <InlineEdit type="text" | "number" | "date" | "textarea" />  for free input
 *   - <InlineSelect options=[…] />                                  for fixed choices
 *
 * Both are uncontrolled w.r.t. their own draft state and only call `onSave`
 * when the user commits. The caller owns the mutation and the displayed
 * value; this component is purely a UI helper.
 */
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Pencil } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

type CommitFn = (next: string | null) => Promise<void> | void;

interface InlineEditCommonProps {
  value: string | null;
  onSave: CommitFn;
  placeholder?: string;
  readOnly?: boolean;
  /** Custom renderer for the display value. Receives the trimmed value or null. */
  display?: (value: string | null) => ReactNode;
  /** Class applied to the display wrapper (e.g. for typography variants). */
  displayClassName?: string;
  /** Class applied to the input in edit mode. */
  inputClassName?: string;
  /** "Add notes" vs. "—" as the empty fallback. */
  emptyVariant?: 'placeholder' | 'dash';
}

interface InlineEditTextProps extends InlineEditCommonProps {
  type?: 'text' | 'number' | 'date';
  numberFormat?: (raw: string) => string | null;
  min?: number;
  max?: number;
  step?: string;
  maxLength?: number;
}

interface InlineEditTextareaProps extends InlineEditCommonProps {
  type: 'textarea';
  rows?: number;
}

type Props = InlineEditTextProps | InlineEditTextareaProps;

const Empty = ({ placeholder, emptyVariant }: { placeholder: string; emptyVariant: 'placeholder' | 'dash' }) =>
  emptyVariant === 'dash' ? (
    <span className="text-muted-foreground">-</span>
  ) : (
    <span className="text-muted-foreground italic">{placeholder}</span>
  );

export function InlineEdit(props: Props) {
  const { t } = useTranslation('productDev');
  const {
    value,
    onSave,
    placeholder = t('inlineEdit.addPlaceholder'),
    readOnly,
    display,
    displayClassName,
    inputClassName,
    emptyVariant = 'placeholder',
  } = props;
  const type = props.type ?? 'text';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  // Local optimistic override so the display flips to the committed value the
  // instant the user commits, instead of waiting on the parent's mutation
  // round-trip. `null` = no override (show the prop). Reconciles (clears)
  // whenever the prop settles to a new value — the parent's optimistic cache
  // patch, or the eventual refetch. Mirrors PriorityFlag's revert pattern.
  const [optimistic, setOptimistic] = useState<{ value: string | null } | null>(null);
  useEffect(() => setOptimistic(null), [value]);
  const shown = optimistic ? optimistic.value : value;
  const shownRef = useRef(shown);
  shownRef.current = shown;
  // Set when a failed save reopens the editor, so the effect keeps the user's
  // attempted text rather than resetting to the (rolled-back) prop value.
  const preserveDraftRef = useRef(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) return;
    if (preserveDraftRef.current) {
      preserveDraftRef.current = false;
    } else {
      setDraft(shownRef.current ?? '');
    }
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      if (el instanceof HTMLInputElement && type !== 'date') {
        el.select();
      }
    });
  }, [editing, type]);

  const commit = useCallback(() => {
    const raw = draft;
    const normalized: string | null = (() => {
      if (type === 'number') {
        const trimmed = raw.trim();
        if (!trimmed) return null;
        // Keep as string — parent decides whether to parseFloat/parseInt.
        return trimmed;
      }
      if (type === 'date') {
        return raw || null;
      }
      const t = raw.trim();
      return t === '' ? null : t;
    })();

    if (normalized === (value ?? null)) {
      setEditing(false);
      return;
    }
    // Optimistic: paint the committed value + leave edit mode immediately; the
    // parent's mutation reconciles the prop. On rejection, revert the paint and
    // reopen the editor with the attempted text so the user can correct + retry.
    setOptimistic({ value: normalized });
    setEditing(false);
    void Promise.resolve(onSave(normalized)).catch((err) => {
      setOptimistic(null);
      preserveDraftRef.current = true;
      setDraft(raw);
      setEditing(true);
      toast({
        title: t('inlineEdit.toast.saveFailed'),
        description: err instanceof Error ? err.message : t('inlineEdit.toast.tryAgain'),
        variant: 'destructive',
      });
    });
  }, [draft, onSave, type, value, t]);

  const cancel = useCallback(() => {
    setEditing(false);
    setDraft(value ?? '');
  }, [value]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
      return;
    }
    if (e.key === 'Enter') {
      if (type === 'textarea' && !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      void commit();
    }
  };

  if (!editing) {
    const filled = shown != null && String(shown).trim() !== '';
    return (
      <button
        type="button"
        onClick={() => !readOnly && setEditing(true)}
        disabled={readOnly}
        className={cn(
          'group relative inline-flex items-center text-left w-full min-w-0 rounded px-1.5 py-1 -mx-1.5 transition-colors',
          !readOnly && 'hover:bg-muted/60 cursor-text',
          readOnly && 'cursor-default',
          displayClassName,
        )}
      >
        <span className="flex-1 min-w-0 truncate">
          {filled ? (
            display ? display(shown) : shown
          ) : (
            <Empty placeholder={placeholder} emptyVariant={emptyVariant} />
          )}
        </span>
        {!readOnly && (
          <Pencil
            className="h-3 w-3 ml-1.5 shrink-0 text-muted-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-hidden
          />
        )}
      </button>
    );
  }

  if (type === 'textarea') {
    const ta = props as InlineEditTextareaProps;
    // `-mx-1.5` + `px-1.5` keep the edit control's text on the same x as the
    // display button (which uses `px-1.5 -mx-1.5`), so clicking doesn't shift
    // the text horizontally.
    return (
      <div className="relative -mx-1.5">
        <Textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit()}
          onKeyDown={handleKeyDown}
          rows={ta.rows ?? 3}
          className={cn('text-sm min-h-[72px] px-1.5 py-1', inputClassName)}
        />
      </div>
    );
  }

  const txt = props as InlineEditTextProps;
  // Match the display button's box: `h-7` (= the display's ~28px height, no
  // row growth) and `px-1.5 -mx-1.5` (= same text x-position, no horizontal
  // jump) so clicking a field doesn't shift content.
  return (
    <div className="relative -mx-1.5">
      <Input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit()}
        onKeyDown={handleKeyDown}
        min={txt.min}
        max={txt.max}
        step={txt.step}
        maxLength={txt.maxLength}
        className={cn('h-7 text-sm px-1.5', inputClassName)}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// InlineSelect — click-to-open dropdown for fixed choices.
// ----------------------------------------------------------------------------

interface InlineSelectProps {
  value: string | null;
  onSave: CommitFn;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  readOnly?: boolean;
  allowClear?: boolean;
  /** Sentinel value used internally for "no selection". */
  display?: (value: string | null, label: string | null) => ReactNode;
  displayClassName?: string;
}

const NONE_SENTINEL = '__none__';

export function InlineSelect({
  value,
  onSave,
  options,
  placeholder,
  readOnly,
  allowClear = true,
  display,
  displayClassName,
}: InlineSelectProps) {
  const { t } = useTranslation('productDev');
  const placeholderText = placeholder ?? t('inlineEdit.selectPlaceholder');
  const [editing, setEditing] = useState(false);
  // Local optimistic override — flips the display to the chosen option instantly
  // and reverts if the parent's mutation rejects (mirrors PriorityFlag / the
  // InlineEdit text variant). Clears whenever the prop settles to a new value.
  const [optimistic, setOptimistic] = useState<{ value: string | null } | null>(null);
  useEffect(() => setOptimistic(null), [value]);
  const shown = optimistic ? optimistic.value : value;

  const label = options.find((o) => o.value === shown)?.label ?? null;

  const commit = useCallback(
    (next: string) => {
      const normalized = next === NONE_SENTINEL ? null : next;
      if (normalized === (value ?? null)) {
        setEditing(false);
        return;
      }
      // Optimistic paint + close; reconcile via the prop, revert on rejection.
      setOptimistic({ value: normalized });
      setEditing(false);
      void Promise.resolve(onSave(normalized)).catch((err) => {
        setOptimistic(null);
        toast({
          title: t('inlineEdit.toast.saveFailed'),
          description: err instanceof Error ? err.message : t('inlineEdit.toast.tryAgain'),
          variant: 'destructive',
        });
      });
    },
    [onSave, value, t],
  );

  if (!editing) {
    const filled = shown != null;
    return (
      <button
        type="button"
        onClick={() => !readOnly && setEditing(true)}
        disabled={readOnly}
        className={cn(
          'group inline-flex items-center gap-1 text-left w-full min-w-0 rounded px-1.5 py-1 -mx-1.5 transition-colors',
          !readOnly && 'hover:bg-muted/60 cursor-pointer',
          readOnly && 'cursor-default',
          displayClassName,
        )}
      >
        <span className="flex-1 min-w-0 truncate">
          {filled ? (
            display ? display(shown, label) : (label ?? shown)
          ) : (
            <span className="text-muted-foreground italic">{placeholderText}</span>
          )}
        </span>
        {!readOnly && (
          <ChevronDown className="h-3 w-3 text-muted-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        )}
      </button>
    );
  }

  return (
    <Select
      defaultOpen
      value={shown ?? NONE_SENTINEL}
      onValueChange={(v) => commit(v)}
      onOpenChange={(open) => {
        if (!open) setEditing(false);
      }}
    >
      {/* Match the display button's box (h-7 + px-1.5 -mx-1.5) so opening the
          dropdown doesn't shift the text or grow the row. */}
      <SelectTrigger className="h-7 text-sm px-1.5 -mx-1.5">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {allowClear && (
          <SelectItem value={NONE_SENTINEL}>
            <span className="text-muted-foreground italic">{t('inlineEdit.noneOption')}</span>
          </SelectItem>
        )}
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
